import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { destinationForUser } from "../../lib/roleRouting";

const LOGO_URL = "https://res.cloudinary.com/dswtir2fh/image/upload/logo_hampwx";
const HERO_IMAGE_URL = "https://res.cloudinary.com/dswtir2fh/image/upload/u14_d9ikyg";

export default function Login() {
    const { login, user, activeClubId } = useAuth();
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (user) {
            navigate(destinationForUser(user, activeClubId), { replace: true });
        }
    }, [user, activeClubId, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        try {
            await login(email, password);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
            <div className="hidden lg:block relative overflow-hidden bg-slate-900">
                <img
                    src={HERO_IMAGE_URL}
                    alt="ASSMBB"
                    className="absolute inset-0 w-full h-full object-cover opacity-35"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/85 to-slate-900/50" />

                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <Link to="/" className="inline-flex items-center gap-3">
                        <img
                            src={LOGO_URL}
                            alt="ASSMBB"
                            className="w-14 h-14 object-contain bg-white rounded-xl p-1.5"
                        />
                        <div>
                            <div className="font-display font-bold text-xl leading-tight">ASSMBB</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-400">
                                Saint Michel Basket-Ball
                            </div>
                        </div>
                    </Link>

                    <div className="space-y-5 max-w-xl">
                        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-orange-400 font-bold">
                            <ShieldCheck className="w-4 h-4" />
                            Espace réservé
                        </div>

                        <h2 className="font-display text-4xl xl:text-6xl font-black leading-tight">
                            Gestion interne du club ASSMBB.
                        </h2>

                        <p className="text-slate-300 max-w-md text-base leading-relaxed">
                            Accès réservé aux responsables autorisés du club :
                            administration, staff et coachs.
                        </p>
                    </div>

                    <div className="text-xs text-slate-400">
                        © 2026 ASSMBB · Antananarivo, Madagascar
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8 flex items-center gap-3">
                        <img
                            src={LOGO_URL}
                            alt="ASSMBB"
                            className="w-14 h-14 object-contain"
                        />
                        <div>
                            <div className="font-display font-bold text-lg">ASSMBB</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                Saint Michel Basket-Ball
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
                        <div className="text-xs uppercase tracking-[0.3em] text-orange-500 font-bold mb-2">
                            Connexion
                        </div>

                        <h1 className="font-display text-3xl sm:text-4xl font-black mb-2 text-slate-900">
                            Espace club
                        </h1>

                        <p className="text-slate-500 mb-8 text-sm">
                            Connectez-vous pour accéder à la gestion ASSMBB.
                        </p>

                        <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                            <label className="block">
                                <span className="text-xs font-semibold text-slate-700 mb-1 block uppercase tracking-wider">
                                    Email
                                </span>
                                <input
                                    data-testid="login-email-input"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                />
                            </label>

                            <label className="block">
                                <span className="text-xs font-semibold text-slate-700 mb-1 block uppercase tracking-wider">
                                    Mot de passe
                                </span>
                                <input
                                    data-testid="login-password-input"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                />
                            </label>

                            {error && (
                                <div
                                    data-testid="login-error"
                                    className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="login-submit-button"
                                className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg px-4 py-2.5 transition-colors disabled:opacity-50"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        Se connecter <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>

                            <Link
                                to="/"
                                className="block text-center text-sm text-slate-500 hover:text-slate-900 mt-4"
                            >
                                ← Retour au site officiel
                            </Link>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
